import { z } from "zod";

import {
  APPLICATION_ROLES,
  COGNITO_CUSTOMER_CASE_SUMMARY_GROUPS,
  COGNITO_INTERNAL_STAFF_GROUPS,
  defineAbilityFor,
} from "@recobra/core";

import { CaseStep, CaseType, EXTERNAL_CODE_MAX } from "../case-core";
import { PaymentTermsSchema } from "../client";
import { NoteSchema } from "../note";
import { LiabilityAnalysisSchema } from "./analysis";
import {
  CaseBaseSchema,
  CaseContactResponseSchema,
  CaseSectionKey,
  CaseSectionReviewSchema,
  DeletedCaseContactSchema,
  DraftDamageEntrySchema,
  DebtSchema,
  DraftDebtSchema,
  DraftIncidentSchema,
  IncidentSchema,
  LiableVehicleSchema,
  RequiredLiableVehiclesSchema,
  RequiredVehicleDetailsSchema,
  VehicleDetailsSchema,
} from "./domain";
import { findDuplicateVehiclePlateIndex } from "./field-validation";
import { CasePaymentPlanSchema } from "./payment";
import { CaseRequirementExceptionSchema } from "./requirements";

const internalStaff = COGNITO_INTERNAL_STAFF_GROUPS;

export const CaseNumberSchema = z.number().int().min(1000);

export const CaseRecordSchema = CaseBaseSchema.partial().extend({
  id: z.uuid().meta({ visibleTo: "*" }),
  caseNumber: CaseNumberSchema.meta({ visibleTo: "*" }),
  clientId: z.uuid().meta({ visibleTo: "*" }),
  branchId: z.uuid().nullish().meta({ visibleTo: "*" }),
  userId: z.string().min(1).meta({ visibleTo: internalStaff }),
  caseType: CaseType.meta({ visibleTo: "*" }),
  step: CaseStep.meta({ visibleTo: "*" }),
  externalCode: z.string().max(EXTERNAL_CODE_MAX).nullish().meta({ visibleTo: internalStaff }),
  incident: DraftIncidentSchema.nullish().meta({
    visibleTo: COGNITO_CUSTOMER_CASE_SUMMARY_GROUPS,
  }),
  liableVehicles: z.array(LiableVehicleSchema).default([]).meta({
    visibleTo: COGNITO_CUSTOMER_CASE_SUMMARY_GROUPS,
  }),
  vehicle: VehicleDetailsSchema.nullish().meta({
    visibleTo: COGNITO_CUSTOMER_CASE_SUMMARY_GROUPS,
  }),
  contacts: z
    .array(CaseContactResponseSchema)
    .nullish()
    .default([])
    .meta({ visibleTo: COGNITO_CUSTOMER_CASE_SUMMARY_GROUPS }),
  archivedContacts: z
    .array(CaseContactResponseSchema)
    .optional()
    .describe("Inactive (deactivated) contacts, readable by whoever could read them while active")
    .meta({ visibleTo: COGNITO_CUSTOMER_CASE_SUMMARY_GROUPS }),
  deletedContacts: z.array(DeletedCaseContactSchema).default([]).meta({ visibleTo: [] }),
  damages: z
    .array(DraftDamageEntrySchema)
    .default([])
    .meta({
      visibleTo: APPLICATION_ROLES.filter((group) =>
        defineAbilityFor({ groups: [group] }).can("read", "Financial"),
      ),
    }),
  debt: DraftDebtSchema.nullish().meta({
    visibleTo: APPLICATION_ROLES.filter((group) =>
      defineAbilityFor({ groups: [group] }).can("read", "Financial"),
    ),
  }),
  paymentPlan: CasePaymentPlanSchema.optional().meta({
    visibleTo: APPLICATION_ROLES.filter((group) =>
      defineAbilityFor({ groups: [group] }).can("read", "Financial"),
    ),
  }),
  clientPaymentTerms: PaymentTermsSchema.optional().meta({
    visibleTo: APPLICATION_ROLES.filter((group) =>
      defineAbilityFor({ groups: [group] }).can("read", "Financial"),
    ),
  }),
  notes: z.array(NoteSchema).default([]).meta({ visibleTo: internalStaff }),
  clientName: z.string().nullish().meta({ visibleTo: "*" }),
  branchName: z.string().nullish().meta({ visibleTo: "*" }),
  createdAt: z.iso.datetime().meta({ visibleTo: "*" }),
  updatedAt: z.iso.datetime().meta({ visibleTo: "*" }),
  creationCompletedAt: z.iso.datetime().nullish().meta({ visibleTo: internalStaff }),
  intakeAnalysis: z
    .object({
      status: z.enum(["pending", "completed", "failed"]),
      updatedAt: z.iso.datetime(),
    })
    .optional()
    .meta({ visibleTo: "*" }),
  requirementExceptions: z
    .array(CaseRequirementExceptionSchema)
    .optional()
    .meta({ visibleTo: internalStaff }),
  liabilityAnalysis: LiabilityAnalysisSchema.optional().meta({
    visibleTo: internalStaff,
  }),
  sectionReviews: z
    .partialRecord(CaseSectionKey, CaseSectionReviewSchema)
    .optional()
    .meta({ visibleTo: internalStaff }),
  activities: z
    .object({
      openTasks: z
        .array(
          z.object({
            id: z.uuid(),
            type: z.string(),
            assignedTo: z.string().nullish(),
            deadlineDate: z.iso.date().nullish(),
          }),
        )
        .default([]),
      totalOpen: z.number(),
    })
    .nullish()
    .meta({ visibleTo: internalStaff }),
});
export type CaseRecord = z.infer<typeof CaseRecordSchema>;

export const CaseSchema = z
  .discriminatedUnion("caseType", [
    CaseRecordSchema.extend({
      caseType: z.literal("third_party_claim").meta({ visibleTo: internalStaff }),
      incident: IncidentSchema.meta({ visibleTo: internalStaff }),
      vehicle: RequiredVehicleDetailsSchema.meta({
        visibleTo: COGNITO_CUSTOMER_CASE_SUMMARY_GROUPS,
      }),
      liableVehicles: RequiredLiableVehiclesSchema.meta({
        visibleTo: COGNITO_CUSTOMER_CASE_SUMMARY_GROUPS,
      }),
      liabilityAnalysis: LiabilityAnalysisSchema.optional().meta({
        visibleTo: internalStaff,
      }),
    }),
    CaseRecordSchema.extend({
      caseType: z.literal("road_conditions").meta({ visibleTo: internalStaff }),
      incident: IncidentSchema.meta({ visibleTo: internalStaff }),
      vehicle: RequiredVehicleDetailsSchema.meta({
        visibleTo: COGNITO_CUSTOMER_CASE_SUMMARY_GROUPS,
      }),
    }),
    CaseRecordSchema.extend({
      caseType: z.literal("zero_km_warranty").meta({
        visibleTo: internalStaff,
      }),
      vehicle: RequiredVehicleDetailsSchema.meta({
        visibleTo: COGNITO_CUSTOMER_CASE_SUMMARY_GROUPS,
      }),
    }),
    CaseRecordSchema.extend({
      caseType: z.literal("routine_maintenance").meta({
        visibleTo: internalStaff,
      }),
      vehicle: RequiredVehicleDetailsSchema.meta({
        visibleTo: COGNITO_CUSTOMER_CASE_SUMMARY_GROUPS,
      }),
    }),
    CaseRecordSchema.extend({
      caseType: z.literal("hidden_defect").meta({
        visibleTo: internalStaff,
      }),
      vehicle: RequiredVehicleDetailsSchema.meta({
        visibleTo: COGNITO_CUSTOMER_CASE_SUMMARY_GROUPS,
      }),
    }),
    CaseRecordSchema.extend({
      caseType: z.literal("debt_collection").meta({ visibleTo: internalStaff }),
      debt: DebtSchema.meta({
        visibleTo: APPLICATION_ROLES.filter((group) =>
          defineAbilityFor({ groups: [group] }).can("read", "Financial"),
        ),
      }),
    }),
  ])
  .superRefine((kase, context) => {
    if (kase.caseType !== "third_party_claim") return;

    const duplicateIndex = findDuplicateVehiclePlateIndex(
      kase.vehicle.licensePlate,
      kase.liableVehicles,
    );
    if (duplicateIndex < 0) return;

    context.addIssue({
      code: "custom",
      message: "case_validation_license_plate_must_be_different",
      path: ["liableVehicles", duplicateIndex, "licensePlate"],
    });
  });
